import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useResumenMesMO } from '@/hooks/useResumenMes';
import type { FiltroVenta } from '@/lib/filtroVenta';
import { cn } from '@/lib/utils';
import { type SemanaOperativa, formatearCRC } from '@adelante/shared';
import { CaretRight, CircleNotch } from '@phosphor-icons/react';
import { useState } from 'react';

/**
 * Resumen del Mes — Mano de Obra: por cada semana operativa del MISMO MES que la
 * seleccionada, muestra MO total gastada, m² construidos y costo MO/m² + el total
 * del mes a la fecha. Reusa useResumenMesMO (caché compartida con el reporte).
 */
export function ResumenMesMOVista({
  semanas,
  semanaSel,
  filtroVenta,
}: {
  semanas: SemanaOperativa[];
  semanaSel: number | null;
  filtroVenta: FiltroVenta;
}) {
  // Carga bajo demanda: el reporte semanal es pesado; solo se consultan las
  // semanas del mes cuando el usuario abre la tabla (no al entrar al tab).
  const [abierto, setAbierto] = useState(false);
  const { filas, tot, cargando } = useResumenMesMO(semanas, semanaSel, filtroVenta, abierto);

  return (
    <Card className="overflow-hidden print:break-inside-avoid print:shadow-none">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-2 border-b border-border bg-zinc-50 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-zinc-100"
      >
        <CaretRight
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', abierto && 'rotate-90')}
          weight="bold"
          aria-hidden="true"
        />
        Resumen del mes — Mano de Obra
        {abierto && cargando && (
          <CircleNotch className="h-3 w-3 animate-spin" weight="bold" aria-hidden="true" />
        )}
        {!abierto && <span className="font-normal normal-case opacity-70">(tocá para cargar)</span>}
      </button>
      {!abierto ? null : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Semana</TableHead>
              <TableHead>Período</TableHead>
              <TableHead className="text-right">Monto Nómina</TableHead>
              <TableHead className="text-right">m² Construidos</TableHead>
              <TableHead className="text-right">Costo por m²</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => {
              const esSel = Number(f.semana.id) === semanaSel;
              return (
                <TableRow
                  key={f.semana.id}
                  className={cn(esSel && 'bg-adelante-primary/20 font-medium')}
                >
                  <TableCell
                    className={cn(
                      'font-mono text-xs font-semibold',
                      esSel && 'border-l-4 border-adelante-primary text-adelante-primary-dark',
                    )}
                  >
                    S{f.semana.numero_semana}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {periodo(f.semana.fecha_inicio)} → {periodo(f.semana.fecha_fin)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-sm">
                    {f.baseSemanal ? formatearCRC(f.moGastada) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-sm">
                    {f.baseSemanal ? fmtNum(f.m2) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-sm">
                    {f.baseSemanal && f.m2 > 0 ? formatearCRC(f.costoM2) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
            {filas.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                  Sin semanas en este mes.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow className="border-t-2">
              <TableCell className="font-semibold" colSpan={2}>
                Total a la fecha
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {formatearCRC(tot.moGastada)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {fmtNum(tot.m2)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {tot.m2 > 0 ? formatearCRC(tot.costoM2) : '—'}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </Card>
  );
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** dd/mm desde un ISO YYYY-MM-DD. */
function periodo(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
}
